/* eslint-disable max-lines -- exceeded max lines allowed */
import { Store } from '@sama/app-state';
import { isEqualAtDepth, mapToObject } from '@sama/js-core';
import {
  cloneDeep,
  forEach,
  forOwn,
  get,
  last,
  matches,
  set,
} from '@sama/micro-dash';
import { pick } from 'lodash';
import { combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
// eslint-disable-next-line import-whitelist -- needed for both core and content
import { getOutputByName } from '../../core/h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { getSharedCssProperty } from '../../outputs/annotation/annotation-shared/properties-shared-with-css';
import { getOutputHelper } from '../../shared/output-helpers/get-output-helper';
import { AnswerOption } from '../../team-apis/otterdam/answer-option';
import { Answer } from '../../team-apis/otterdam/answers/answer';
import {
  FrameSpecificTag,
  LocationExternal,
  PointType,
  ShapeLocationExternal,
  ShapeType,
  TagLocation,
} from '../../team-apis/otterdam/answers/shape-typings';
import { AnnotationOutput } from '../../team-apis/otterdam/outputs/annotation-output';
import {
  AbstractOutput,
  AbstractOutputWithAnswerOptions,
  Output,
} from '../../team-apis/otterdam/outputs/output';
import { AnnotationState } from '../annotation/shared/state/annotation-state';
import {
  ShapeState,
  Tag,
  TagsState,
} from '../annotation/shared/state/shape-state';
import {
  findInsertPoint,
  getEffectiveItem,
} from '../annotation/shared/utils/keyframe-utils';
import { TRAINING_SHAPE_COLOR } from '../constants';
import { ObjectWith } from '../types/misc';

export const isSummaryRegex = /^#/;

export function getDynamicTag(
  frameSpecificTags: FrameSpecificTag[] = [],
  frameNumber: number,
): Tag | undefined {
  const tagInfo = getEffectiveItem(frameSpecificTags, frameNumber);
  return tagInfo?.value;
}

/** @deprecated used for old projects without objects */
export function getShapeColorObjectless$(
  shapeStore: Store<ShapeState>,
  frameNumberStore: Store<number>,
  output: AnnotationOutput,
  colorOutput?: AbstractOutputWithAnswerOptions,
) {
  if (shapeStore.state().transparent) {
    return of('#0000');
  }

  const defaultShapeColor = `rgb(${getSharedCssProperty('default-shape-rgb')})`;
  if (!colorOutput) {
    return of(defaultShapeColor);
  }

  const colorOptions = colorOutput.answer_options;
  return getTag$(
    colorOutput,
    shapeStore,
    frameNumberStore,
    output.answer_options,
  ).pipe(
    map((tag: any) => {
      const color = get(colorOptions.find(matches({ name: tag })), ['color']);
      return color || defaultShapeColor;
    }),
  );
}

export function getTrainingShapeColor(shapeStore: Store<ShapeState>) {
  return shapeStore.state().transparent ? '#0000' : TRAINING_SHAPE_COLOR;
}

export function getTag$(
  output: AbstractOutput,
  tagsStore: Store<TagsState>,
  frameNumberStore: Store<number>,
  answerOptions: AnswerOption[],
) {
  if (isDynamicOutput(output.name, answerOptions)) {
    return combineLatest([
      tagsStore('dynamic_tags')(output.name).$,
      frameNumberStore.$,
    ]).pipe(
      filter(() => !!tagsStore.state()),
      map(([frameSpecificTags, frameNumber]) =>
        getDynamicTag(frameSpecificTags, frameNumber),
      ),
      distinctUntilChanged(),
    );
  } else {
    return tagsStore<'tags', ObjectWith<Answer>>('tags')(output.name).$.pipe(
      filter(() => !!tagsStore.state()),
    );
  }
}

export function isDynamicOutput(name: string, options: AnswerOption[]) {
  const answerOption = options.find(matches({ name }));
  if (!answerOption) {
    throw new Error(`No answer option named ${name}`);
  }

  return answerOption.dynamic;
}

export function getColorOutput(
  colorOutputId: number | null,
  outputs: Output[],
) {
  if (!colorOutputId) {
    return undefined;
  }
  return outputs.find(
    matches({ id: colorOutputId }),
  ) as AbstractOutputWithAnswerOptions;
}

export function addDynamicTagsToKeyLocations<P extends PointType>(
  dynamicTags: ObjectWith<FrameSpecificTag[]>,
  keyLocations: Array<ShapeLocationExternal<P>>,
) {
  const newKeyLocations: Array<LocationExternal<P>> = cloneDeep(keyLocations);
  forOwn(dynamicTags, (frameSpecificTags, outputName) => {
    forEach(frameSpecificTags, (fst) => {
      const insertPoint = findInsertPoint(newKeyLocations, fst.frame_number);
      const keyLocationAtInsertPoint = newKeyLocations[insertPoint];
      if (
        get(keyLocationAtInsertPoint, ['frame_number']) === fst.frame_number
      ) {
        set(keyLocationAtInsertPoint, ['tags', outputName], fst.value);
      } else {
        newKeyLocations.splice(insertPoint, 0, {
          frame_number: fst.frame_number,
          tags: { [outputName]: fst.value },
        });
      }
    });
  });
  return newKeyLocations;
}

/**
 * Gets dynamic tag from key location item and populates the dynamic_tags
 * property, which store relies on. Assumes `dynamicTags` is always kept in
 * order by frame number (and preserves it).
 */
export function extractDynamicTagsFromKeyLocation<
  P extends PointType,
  S extends ShapeType,
>(keyLocation: TagLocation, dynamicTags: ShapeState<P, S>['dynamic_tags']) {
  forOwn(keyLocation.tags, (tag, outputName) => {
    let frameSpecificTags = dynamicTags[outputName];
    // fix me if you change this code

    if (!frameSpecificTags) {
      frameSpecificTags = dynamicTags[outputName] = [];
    }
    const frameSpecificTag = last(frameSpecificTags);

    if (get(frameSpecificTag, 'frame_number') === keyLocation.frame_number) {
      frameSpecificTag.value = tag;
    } else {
      frameSpecificTags.push({
        frame_number: keyLocation.frame_number,
        value: tag,
      });
    }
  });
}

export function areTagsStatesEqual(x: TagsState, y: TagsState) {
  return isEqualAtDepth(
    2,
    pick(x, 'tags', 'dynamic_tags'),
    pick(y, 'tags', 'dynamic_tags'),
  );
}

export function setDefaultTags(
  shapeOrObject: AnnotationState,
  answerOptions: AnswerOption[],
  outputs: Output[],
  frameNumber: number,
) {
  const tagsWithDefaultAnswer: StrictRecord<AbstractOutput['name'], string> =
    {};
  answerOptions.forEach((option) => {
    const defaultAnswerId = getDefaultAnswerIdForOutput(option.name, outputs);
    if (defaultAnswerId && !isDependentOutput(option.name, outputs)) {
      const childOutputs = getChildOutputs(
        option.name,
        defaultAnswerId,
        outputs,
      );
      Object.assign(
        tagsWithDefaultAnswer,
        getDefaultTagsForShape([option.name, ...childOutputs], outputs),
      );
    }
  });
  assignTags(tagsWithDefaultAnswer, shapeOrObject, answerOptions, frameNumber);
}

function assignTags(
  tags: StrictRecord<AbstractOutput['name'], string>,
  shapeOrObject: AnnotationState,
  answerOptions: AnswerOption[],
  frameNumber: number,
) {
  Object.entries(tags).forEach(([tagName, value]) => {
    if (isDynamicOutput(tagName, answerOptions)) {
      shapeOrObject.dynamic_tags[tagName] ??= [
        {
          frame_number: frameNumber,
          value,
        },
      ];
    } else {
      shapeOrObject.tags[tagName] ??= value;
    }
  });
}

function isDependentOutput(outputName: string, outputs: Output[]) {
  return (
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    getOutputByName({ outputs }, outputName)?.depends_on_answers_from_output_id
  );
}

export function isSupportedOutputForDefaultTags(
  output: AbstractOutput,
): output is AbstractOutputWithAnswerOptions {
  return getOutputHelper(output.type).supportsDefaultAnswer;
}

function getDefaultTagsForShape(outputNames: string[], outputs: Output[]) {
  const answerOptionsWithDefaultAnswer = outputNames
    .map((outputName) => getOutputByName({ outputs }, outputName))
    .filter(
      isSupportedOutputForDefaultTags,
      // Only used on objectless, will be removed eventually
    ) as AbstractOutputWithAnswerOptions[];
  return mapToObject(answerOptionsWithDefaultAnswer, (answerOption) => [
    answerOption.name,
    answerOption.answer_options[0].name,
  ]);
}

function getChildOutputs(
  outputName: string,
  defaultAnswerId: number,
  outputs: Output[],
) {
  const outputId = getOutputByName({ outputs }, outputName).id;
  return outputs
    .filter(
      (o) =>
        o.depends_on_answers_from_output_id === outputId &&
        isSupportedOutputForDefaultTags(o) &&
        o.meta?.depends_on_answer_option_ids?.includes(defaultAnswerId),
    )
    .map((o) => o.name);
}

function getDefaultAnswerIdForOutput(outputName: string, outputs: Output[]) {
  const output = getOutputByName({ outputs }, outputName);
  if (isSupportedOutputForDefaultTags(output)) {
    return output.answer_options[0].id;
  }
  return;
}
